import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  ButtonBase,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
  Skeleton,
  Stack,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';

import { salonApi, Salon, SalonService } from '../services/salonService';
import { staffApi, Staff } from '../services/staffService';
import { bookingApi } from '../services/bookingService';
import { formatServicePrice } from '../services/serviceService';
import { PageHeader, SectionCard, EmptyState, LoadingState, PersonAvatar } from '../components/ui';
import { tokens } from '../theme';
import { formatDayMonthShort, formatDuration, formatWeekdayDay } from '../utils/format';

const steps = ['Usługa', 'Specjalista', 'Termin', 'Potwierdzenie'];

const WEEKDAY_SHORT = ['nd', 'pn', 'wt', 'śr', 'cz', 'pt', 'sb'];

const pad = (n: number) => String(n).padStart(2, '0');
const toIsoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** YYYY-MM-DD jako data lokalna (poludnie, zeby strefa czasowa nie przesunela dnia). */
const fromIsoDate = (iso: string) => new Date(`${iso}T12:00:00`);

/** Kafelek wyboru (usluga, specjalista) — cala powierzchnia klikalna, zaznaczenie sliwka. */
const OptionTile: React.FC<{ selected: boolean; onClick: () => void; children: React.ReactNode }> = ({ selected, onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      textAlign: 'left',
      p: 2,
      borderRadius: '12px',
      border: `1px solid ${selected ? tokens.plum : tokens.line}`,
      boxShadow: selected ? `inset 0 0 0 1px ${tokens.plum}` : 'none',
      bgcolor: selected ? tokens.plumSofter : tokens.paper,
      transition: 'border-color .15s ease, background-color .15s ease',
      '&:hover': { borderColor: selected ? tokens.plum : tokens.lineStrong, bgcolor: selected ? tokens.plumSofter : tokens.surface },
    }}
  >
    {children}
  </ButtonBase>
);

const SummaryRow: React.FC<{ label: string; value?: React.ReactNode; hint?: React.ReactNode }> = ({ label, value, hint }) => (
  <Box>
    <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontWeight: 600 }}>{label}</Typography>
    {value ? (
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 650, color: tokens.ink }}>{value}</Typography>
    ) : (
      <Typography sx={{ fontSize: '0.9375rem', color: tokens.subtle }}>Nie wybrano</Typography>
    )}
    {value && hint && <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>{hint}</Typography>}
  </Box>
);

export const BookingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const salonId = searchParams.get('salonId') || '';

  const [activeStep, setActiveStep] = useState(0);
  const [salon, setSalon] = useState<Salon | null>(null);
  const [services, setServices] = useState<SalonService[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Selections
  const [selectedService, setSelectedService] = useState<SalonService | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!salonId) return;
    const load = async () => {
      try {
        const [salonData, serviceData, staffData] = await Promise.all([
          salonApi.getById(salonId),
          // We need services from the salon detail
          salonApi.getById(salonId),
          staffApi.listBySalon(salonId),
        ]);
        setSalon(salonData);
        setServices(salonData.services || []);
        setStaffList(staffData);
      } catch {
        setError('Nie udało się załadować danych salonu');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [salonId]);

  useEffect(() => {
    if (!selectedService || !selectedStaff || !selectedDate) return;
    const loadSlots = async () => {
      setSlotsLoading(true);
      setSelectedTime('');
      try {
        const slots = await bookingApi.getAvailableSlots({
          salonId,
          staffId: selectedStaff.id,
          serviceId: selectedService.id,
          date: selectedDate,
        });
        setAvailableSlots(slots);
      } catch {
        setAvailableSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };
    loadSlots();
  }, [salonId, selectedService, selectedStaff, selectedDate]);

  const handleBook = async () => {
    if (!selectedService || !selectedStaff || !selectedDate || !selectedTime) return;
    setError('');
    setSubmitting(true);
    try {
      await bookingApi.create({
        salonId,
        serviceId: selectedService.id,
        staffId: selectedStaff.id,
        date: selectedDate,
        startTime: selectedTime,
        notes: notes || undefined,
      });
      setSuccess(true);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Nie udało się zarezerwować wizyty. Wybierz inny termin albo spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  })();

  const breadcrumbs = [{ label: 'Znajdź salon', to: '/salons' }];

  if (!salonId) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Rezerwacja wizyty" description="Rezerwację zaczniesz od wyboru salonu." />
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Najpierw wybierz salon"
            description="Otwórz salon z listy i kliknij „Zarezerwuj wizytę”, żeby wybrać usługę i termin."
            actions={<Button variant="contained" onClick={() => navigate('/salons')}>Przejdź do listy salonów</Button>}
          />
        </SectionCard>
      </Box>
    );
  }

  if (loading) {
    return (
      <Stack gap={2.5} aria-busy="true">
        <Skeleton width={260} height={44} />
        <LoadingState variant="cards" count={4} />
      </Stack>
    );
  }

  if (!salon) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Rezerwacja wizyty" />
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Nie udało się załadować salonu"
            description={`${error || 'Salon jest niedostępny.'} Odśwież stronę albo wybierz salon z listy.`}
            actions={<Button variant="outlined" onClick={() => navigate('/salons')}>Wróć do listy salonów</Button>}
          />
        </SectionCard>
      </Box>
    );
  }

  const dateLabel = selectedDate ? formatWeekdayDay(fromIsoDate(selectedDate)) : '';

  if (success) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title={salon.name} description="Rezerwacja wizyty" />
        <SectionCard sx={{ maxWidth: 560, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', py: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: '50%',
                bgcolor: tokens.successSoft, color: tokens.success, display: 'grid', placeItems: 'center',
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography component="h2" variant="h2">Wizyta zarezerwowana</Typography>
            <Typography sx={{ mt: 0.75, color: tokens.inkSoft }}>
              Szczegóły i status wizyty znajdziesz w zakładce Moje wizyty.
            </Typography>
            <Box sx={{ mt: 2.5, mx: 'auto', maxWidth: 380, bgcolor: tokens.ground, borderRadius: '12px', px: 2, py: 1.75, textAlign: 'left' }}>
              <Typography sx={{ fontWeight: 700 }}>{selectedService?.name}</Typography>
              <Typography sx={{ color: tokens.inkSoft, '&::first-letter': { textTransform: 'uppercase' } }}>
                {dateLabel}, {selectedTime}
              </Typography>
              <Typography sx={{ color: tokens.muted, fontSize: '0.875rem' }}>
                {salon.name} · {selectedStaff?.firstName} {selectedStaff?.lastName}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="center" sx={{ mt: 3 }}>
              <Button variant="contained" onClick={() => navigate('/bookings/my')}>
                Przejdź do moich wizyt
              </Button>
              <Button variant="outlined" onClick={() => navigate(`/salons/${salonId}`)}>
                Wróć do salonu
              </Button>
            </Stack>
          </Box>
        </SectionCard>
      </Box>
    );
  }

  const activeServices = services.filter((s) => s.isActive);
  const serviceGroups: { category: string; items: SalonService[] }[] = [];
  activeServices.forEach((svc) => {
    const category = svc.category || 'Pozostałe';
    const group = serviceGroups.find((g) => g.category === category);
    if (group) group.items.push(svc);
    else serviceGroups.push({ category, items: [svc] });
  });
  const showCategories = serviceGroups.length > 1;

  const availableStaff = staffList.filter((s) => s.isActive && (!selectedService || s.services?.some((ss) => ss.service.id === selectedService.id)));

  // Najblizsze 14 dni od pierwszego dostepnego dnia — szybki wybor bez kalendarza.
  const quickDays = Array.from({ length: 14 }, (_, i) => {
    const d = fromIsoDate(minDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  const slotGroups = [
    { label: 'Rano', items: availableSlots.filter((s) => s < '12:00') },
    { label: 'Po południu', items: availableSlots.filter((s) => s >= '12:00' && s < '17:00') },
    { label: 'Wieczorem', items: availableSlots.filter((s) => s >= '17:00') },
  ].filter((g) => g.items.length > 0);

  const stepTitles = ['Wybierz usługę', 'Wybierz specjalistę', 'Wybierz termin', 'Sprawdź i potwierdź'];
  const stepSubtitles = [
    'Cena i czas trwania są podane przy każdej usłudze.',
    selectedService ? `Osoby, które wykonują: ${selectedService.name}` : undefined,
    'Pokazujemy tylko wolne godziny wybranej osoby.',
    'Po potwierdzeniu wizyta pojawi się w zakładce Moje wizyty.',
  ];

  const footer = (content: React.ReactNode) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap={1}
      flexWrap="wrap"
      sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${tokens.line}` }}
    >
      {content}
    </Stack>
  );

  const backButton = (step: number) => (
    <Button variant="text" startIcon={<ArrowBackRoundedIcon />} onClick={() => setActiveStep(step)} disabled={submitting}>
      Wstecz
    </Button>
  );

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={salon.name}
        description={`Rezerwacja wizyty · ${salon.address}, ${salon.city}`}
      >
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>{steps[activeStep]}</Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontWeight: 600 }}>
              Krok {activeStep + 1} z {steps.length}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={((activeStep + 1) / steps.length) * 100} />
        </Box>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 300px' },
          alignItems: 'start',
        }}
      >
        <SectionCard title={stepTitles[activeStep]} subtitle={stepSubtitles[activeStep]} sx={{ minWidth: 0 }}>
          {/* Step 0: Choose service */}
          {activeStep === 0 && (
            activeServices.length === 0 ? (
              <EmptyState
                compact
                icon={<ContentCutOutlinedIcon />}
                title="Ten salon nie ma jeszcze usług do rezerwacji"
                description="Wybierz inny salon albo zadzwoń do salonu, żeby umówić wizytę."
                actions={<Button variant="outlined" onClick={() => navigate('/salons')}>Wróć do listy salonów</Button>}
              />
            ) : (
              <Stack gap={2}>
                {serviceGroups.map((group) => (
                  <Box key={group.category}>
                    {showCategories && (
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 650, color: tokens.muted, mb: 1 }}>{group.category}</Typography>
                    )}
                    <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                      {group.items.map((service) => (
                        <OptionTile
                          key={service.id}
                          selected={selectedService?.id === service.id}
                          onClick={() => {
                            setSelectedService(service);
                            setActiveStep(1);
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{service.name}</Typography>
                          {(service.publicDescription || service.description) && (
                            <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, mt: 0.25 }}>
                              {service.publicDescription || service.description}
                            </Typography>
                          )}
                          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mt: 'auto', pt: 1.25 }}>
                            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: tokens.muted }}>
                              <AccessTimeRoundedIcon sx={{ fontSize: 16 }} />
                              <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>{formatDuration(service.duration)}</Typography>
                            </Stack>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
                              {formatServicePrice(service)}
                            </Typography>
                          </Stack>
                        </OptionTile>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )
          )}

          {/* Step 1: Choose staff */}
          {activeStep === 1 && (
            <>
              {availableStaff.length === 0 ? (
                <EmptyState
                  compact
                  icon={<GroupsOutlinedIcon />}
                  title="Nikt nie wykonuje tej usługi online"
                  description="Wybierz inną usługę albo skontaktuj się z salonem."
                />
              ) : (
                <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                  {availableStaff.map((staff) => (
                    <OptionTile
                      key={staff.id}
                      selected={selectedStaff?.id === staff.id}
                      onClick={() => {
                        setSelectedStaff(staff);
                        setActiveStep(2);
                      }}
                    >
                      <Stack direction="row" gap={1.5} alignItems="flex-start">
                        <PersonAvatar firstName={staff.firstName} lastName={staff.lastName} seed={staff.id} size={44} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                            {staff.firstName} {staff.lastName}
                          </Typography>
                          {(staff.specialization || staff.bio) && (
                            <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, mt: 0.25 }}>
                              {staff.specialization || staff.bio}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </OptionTile>
                  ))}
                </Box>
              )}
              {footer(backButton(0))}
            </>
          )}

          {/* Step 2: Choose date & time */}
          {activeStep === 2 && (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>Dzień</Typography>
              <Box
                role="group"
                aria-label="Najbliższe dni"
                sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, WebkitOverflowScrolling: 'touch' }}
              >
                {quickDays.map((d) => {
                  const iso = toIsoDate(d);
                  const selected = iso === selectedDate;
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <ButtonBase
                      key={iso}
                      onClick={() => setSelectedDate(iso)}
                      aria-pressed={selected}
                      aria-label={formatWeekdayDay(d)}
                      sx={{
                        flexShrink: 0,
                        width: 58,
                        py: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '12px',
                        border: `1px solid ${selected ? tokens.plum : tokens.lineStrong}`,
                        bgcolor: selected ? tokens.plum : tokens.paper,
                        color: selected ? tokens.paper : tokens.ink,
                        transition: 'background-color .15s ease, border-color .15s ease',
                        '&:hover': { bgcolor: selected ? tokens.plumDark : tokens.plumSofter, borderColor: selected ? tokens.plumDark : tokens.plumLight },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 650, color: selected ? 'inherit' : weekend ? tokens.subtle : tokens.muted }}>
                        {WEEKDAY_SHORT[d.getDay()]}
                      </Typography>
                      <Typography sx={{ fontSize: '1.125rem', fontWeight: 750, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>
                        {d.getDate()}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: selected ? 'inherit' : tokens.muted }}>
                        {formatDayMonthShort(d).split(' ')[1]}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>

              <TextField
                type="date"
                label="Albo wybierz inną datę"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                inputProps={{ min: minDate }}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 1.5, width: { xs: '100%', sm: 240 } }}
              />

              <Box sx={{ mt: 3 }}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Godzina</Typography>
                  {selectedDate && (
                    <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, '&::first-letter': { textTransform: 'uppercase' } }}>
                      {dateLabel}
                    </Typography>
                  )}
                </Stack>

                {!selectedDate && (
                  <Box sx={{ bgcolor: tokens.surface, border: `1px dashed ${tokens.lineStrong}`, borderRadius: '12px', px: 2, py: 2.5, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.9375rem', color: tokens.inkSoft }}>Wybierz dzień, żeby zobaczyć wolne godziny.</Typography>
                  </Box>
                )}

                {slotsLoading && (
                  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))' }} aria-busy="true">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Skeleton key={i} variant="rounded" height={44} />
                    ))}
                  </Box>
                )}

                {!slotsLoading && selectedDate && availableSlots.length > 0 && (
                  <Stack gap={2}>
                    {slotGroups.map((group) => (
                      <Box key={group.label}>
                        <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontWeight: 600, mb: 0.75 }}>{group.label}</Typography>
                        <Box
                          role="group"
                          aria-label={`Wolne godziny — ${group.label.toLowerCase()}`}
                          sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))' }}
                        >
                          {group.items.map((slot) => {
                            const selected = selectedTime === slot;
                            return (
                              <ButtonBase
                                key={slot}
                                onClick={() => setSelectedTime(slot)}
                                aria-pressed={selected}
                                sx={{
                                  height: 44,
                                  borderRadius: '10px',
                                  border: `1px solid ${selected ? tokens.plum : tokens.lineStrong}`,
                                  bgcolor: selected ? tokens.plum : tokens.paper,
                                  color: selected ? tokens.paper : tokens.ink,
                                  fontSize: '0.9375rem',
                                  fontWeight: 650,
                                  fontVariantNumeric: 'tabular-nums',
                                  transition: 'background-color .15s ease, border-color .15s ease',
                                  '&:hover': { bgcolor: selected ? tokens.plumDark : tokens.plumSofter, borderColor: selected ? tokens.plumDark : tokens.plumLight },
                                }}
                              >
                                {slot}
                              </ButtonBase>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                {!slotsLoading && selectedDate && availableSlots.length === 0 && (
                  <EmptyState
                    compact
                    icon={<EventBusyOutlinedIcon />}
                    title="Brak wolnych godzin tego dnia"
                    description="Wybierz inny dzień albo inną osobę z zespołu."
                    sx={{ bgcolor: tokens.surface, borderRadius: '12px' }}
                  />
                )}
              </Box>

              <TextField
                label="Uwagi do wizyty"
                placeholder="np. alergie, preferencje — opcjonalnie"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 3 }}
              />

              {footer(
                <>
                  {backButton(1)}
                  <Button variant="contained" disabled={!selectedTime} onClick={() => setActiveStep(3)}>
                    Dalej
                  </Button>
                </>,
              )}
            </Box>
          )}

          {/* Step 3: Confirmation */}
          {activeStep === 3 && (
            <Box>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                <SummaryRow label="Salon" value={salon.name} hint={`${salon.address}, ${salon.city}`} />
                <SummaryRow label="Usługa" value={selectedService?.name} />
                <SummaryRow label="Specjalista" value={selectedStaff ? `${selectedStaff.firstName} ${selectedStaff.lastName}` : undefined} />
                <SummaryRow
                  label="Termin"
                  value={selectedDate && selectedTime ? <Box component="span" sx={{ display: 'inline-block', '&::first-letter': { textTransform: 'uppercase' } }}>{dateLabel}, {selectedTime}</Box> : undefined}
                />
                <SummaryRow label="Czas trwania" value={selectedService ? formatDuration(selectedService.duration) : undefined} />
                <SummaryRow label="Cena" value={selectedService ? formatServicePrice(selectedService) : undefined} />
                {notes && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontWeight: 600 }}>Uwagi</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', whiteSpace: 'pre-wrap' }}>{notes}</Typography>
                  </Box>
                )}
              </Box>
              {footer(
                <>
                  {backButton(2)}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBook}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {submitting ? 'Rezerwowanie…' : 'Zarezerwuj wizytę'}
                  </Button>
                </>,
              )}
            </Box>
          )}
        </SectionCard>

        {/* Podsumowanie wyboru — na telefonie wystarcza ostatni krok */}
        <SectionCard title="Twoja wizyta" sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 88 }}>
          <Stack gap={1.75}>
            <SummaryRow
              label="Usługa"
              value={selectedService?.name}
              hint={selectedService ? `${formatDuration(selectedService.duration)} · ${formatServicePrice(selectedService)}` : undefined}
            />
            <SummaryRow label="Specjalista" value={selectedStaff ? `${selectedStaff.firstName} ${selectedStaff.lastName}` : undefined} />
            <SummaryRow
              label="Termin"
              value={selectedDate && selectedTime ? <Box component="span" sx={{ display: 'inline-block', '&::first-letter': { textTransform: 'uppercase' } }}>{dateLabel}, {selectedTime}</Box> : undefined}
            />
          </Stack>
        </SectionCard>
      </Box>
    </Box>
  );
};
